import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { TsForm } from '@/components/form/Form';
import { SubmitCancelButtons } from '@/components/form/SubmitCancelButtons';
import { TextField } from '@/components/form/TextInput';
import { Modal } from '@/components/shared/modal/Modal';
import { handleMutationResult } from '@/helpers/toastHelper';
import { defaultProperties, LeaveType } from '@/services/leaveManagement/leave.types';
import { useCreateLeaveType, useLeaveTypes } from '@/services/leaveManagement/LeaveTypesProvider';
import { strings } from '@/services/translation/strings';
import { FunctionComponent } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import adminStrings from '../admin.translations';

interface AddLeaveTypeFormValues {
  name: string;
}

interface AdminAddLeaveTypeModalProps {
  open: boolean;
  onClose: () => void;
}

const AdminAddLeaveTypeModal: FunctionComponent<AdminAddLeaveTypeModalProps> = ({ open, onClose }) => {
  const navigate = useNavigate();

  const { fetching, create } = useCreateLeaveType();
  const methods = useForm<AddLeaveTypeFormValues>();

  const leaveTypes = useLeaveTypes();

  const onSubmit = async (values: AddLeaveTypeFormValues) => {
    const newLeaveType: LeaveType = {
      id: '',
      name: values.name,
      active: true,
      properties: { ...defaultProperties, displayName: values.name }
    };

    const result = await create(newLeaveType);

    await handleMutationResult(result, {
      successMessage: 'Leave Type saved successfully.',
      errorMessage: 'Failed to save Leave Type.',
      customSuccessCheck: () => !!result.data?.createLeaveType,
      onSuccess: async () => {
        methods.reset();
        if (result.data?.createLeaveType) {
          const newLeaveTypeId = result.data?.createLeaveType.id;
          navigate(`${newLeaveTypeId}`, { state: { leaveType: result.data.createLeaveType } });
        }
      }
    });
  };

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} header={adminStrings.leaveTypeSection.addDialog.title}>
      <SubmissionErrorBoundary>
        <TsForm methods={methods} onSubmit={onSubmit}>
          <div className="flex flex-col space-y-3 w-full">
            <TextField
              label={adminStrings.leaveTypeSection.addDialog.newLeaveType}
              name="name"
              id="name"
              placeholder={adminStrings.leaveTypeSection.addDialog.leaveTypeName}
              customValidatorMessage={methods.formState.errors.name?.message}
              required
              rules={{
                required: adminStrings.leaveTypeSection.addDialog.nameRequired,
                validate: (value: string) => {
                  const isDuplicate = leaveTypes?.some(l => l?.name === value);
                  if (isDuplicate) return adminStrings.leaveTypeSection.addDialog.uniqueName;
                  return true;
                }
              }}
            />

            <div className="flex items-center justify-end gap-3">
              <SubmitCancelButtons
                disabled={!!methods.formState.errors.name?.message}
                onComplete={handleClose}
                loading={fetching}
                submitText={strings.common.create}
              />
            </div>
          </div>
        </TsForm>
      </SubmissionErrorBoundary>
    </Modal>
  );
};

export default AdminAddLeaveTypeModal;
